/**
 * The two shaders a tracer is drawn with: a **stretched ribbon** for its tail
 * and a **camera-facing glow** for its head, plus the four material flags this
 * renderer has broken four times.
 *
 * Split out of `tracer-pool.ts` on 2026-08-25, when the shading became a
 * subject in its own right rather than a detail of the scene graph. Three
 * modules now, one question each: `flight/tracer-tint.ts` owns *what colour* a
 * side's rung is, this file owns *where in the round that colour goes*, and
 * `tracer-pool.ts` owns the meshes and the instance matrices. The middle
 * question is the one the 2026-08-25 legibility report turned out to be about,
 * and it is not answerable from either of the other two files.
 *
 * ## Where the side reads, and the 2026-08-25 rearrangement
 *
 * A tracer's colour depends on who fired it, and `flight/tracer-tint.ts` picks
 * the rung. **How much of the round that rung is worth is decided here**, and
 * until 2026-08-25 the answer was: not much. Both `colorNode`s put the *shared*
 * white where the intensity was and the *per-side* rung where it was not — the
 * head mixed `tint -> TRACER_HOT_COLOUR` on the falloff, i.e. strongest at the
 * rim where the same falloff, cubed, had already taken the intensity to zero;
 * the tail mixed on `hot²` while its intensity ramps as `hot³`. Integrated over
 * the two quads the tint carried **30.4 %** of the head's emitted luminance and
 * **26.6 %** of the tail's, so three pages of palette derivation were being
 * applied to a quarter of the picture. The user flew against it and could not
 * tell enemy fire from his own.
 *
 * Both were rearranged to **85.8 %** and **57.3 %**, at unchanged total intensity —
 * see each builder's own doc. `docs/engineering/enhanced-tracers.md` §2.0 and
 * §3.4 carry the post-mortem and the arithmetic; `apps/web/test/tracer-
 * legibility.ts` keeps a model of the *previous* shading so the gain is a
 * measured difference rather than a claim.
 *
 * ## The material — measured on the swapchain, and it is not what was expected
 *
 * A tracer is a **light**: it has to *add* to whatever is behind it, or it is a
 * decal. Getting that took an A/B on the real GPU rather than a reading of the
 * source, and it overturned the arrangement this file was first written with.
 *
 * `searchlight-beams.ts` records that `WebGPUPipelineUtils.js` programs a
 * material's blend state when `material.blending !== NoBlending &&
 * (material.blending !== NormalBlending || material.transparent !== false)`,
 * i.e. that `AdditiveBlending` on an **opaque** material is a real combination.
 * That condition is still there in the installed three (0.185.1) and this file
 * originally trusted it. **Measured, it does not blend.** The instrument was one
 * frame of a burst with the head's shading replaced by a flat `0.35`: over the
 * pale enhanced sky the quads came back at RGB **(86, 68, 0)**, which is
 * `#f7c300 × 0.35` exactly — the mid rung of the day, and the fragment
 * *replacing* the destination.
 * Additive over that sky would have been (255, 255, 200). The visible symptom
 * was a **black halo** around every round, RGB (1, 1, 0) where the falloff went
 * to nothing: the glow's own dark outskirts, painted opaquely.
 *
 * With `transparent: true` the same frame composites correctly — the head reads
 * as sky-plus-light and the tail glows warm over the green. So on this backend
 * the blend state follows the *pass*, not just the flag, and an additive effect
 * has to be in the transparent pass to be additive at all.
 *
 * That runs straight into the other rule, which is why it needed measuring
 * rather than choosing: `transparent: true` and `depthWrite: false` each
 * rasterized **zero pixels** on a pooled `InstancedMesh` under this renderer's
 * reversed-Z buffer on four recorded occasions (`docs/engineering/lessons.md`;
 * one of them is the bullet pool itself, pinned by
 * `apps/web/test/bullet-material.test.ts`). Both flags were therefore A/B'd
 * here, separately, on the swapchain — never in a `RenderTarget`, which that
 * file's third rule says is not a control for "is it on screen":
 *
 * - `transparent: true`, `depthWrite: true` — **draws**, and blends additively.
 * - `transparent: true`, `depthWrite: false` — **draws**, blends additively,
 *   and is the shipped pair. Peak red over the frame **255**.
 *
 * The trap is real and this is not a licence to re-apply those flags anywhere
 * else. The lessons entry is precise about its own scope — the pool that
 * vanished was "in the scene graph before it had a transform to draw", and six
 * meshes added to a live scene with the same flags drew fine — and these two
 * meshes are `visible = false` until `place` has given them instances, so they
 * never enter a frame's render list untransformed. What carries forward is the
 * *rule*, which the 2026-08-22 smoke lesson already sharpened into its final
 * form: **measure the flag where you are using it, and take the measurement
 * even when you are confident**, because the same flag is not fatal in the same
 * way in two places. This lane took it and the answer was the opposite of the
 * expectation in both directions at once.
 *
 * `depthWrite: false` is also what the effect actually wants, and it deletes a
 * problem rather than trading one for another. The head quad and the tail
 * ribbon meet *at* the round, where their depths are equal — two depth-writing
 * surfaces at equal depth being the one z-fighting tie that
 * `docs/engineering/lessons.md` names as unreachable from the scene graph. With
 * neither writing, they simply both add, which is what a glow and its trail
 * should do anyway, and no depth bias has to be invented to separate them.
 * `depthTest` stays on, so an aeroplane in front of a tracer still hides it.
 *
 * Brightness lives in the **rgb**, not the alpha: the colour node is already
 * multiplied by its falloff, so the blend adds exactly that whatever the
 * backend decides `srcAlpha` is. The alpha is a plain 0/1 coverage mask cut by
 * `alphaTest`, which only saves the shading of fragments that would add
 * nothing.
 *
 * `forceSinglePass`, finally, is worth two draw calls. A **double-sided
 * transparent** material makes this renderer draw the object twice — back faces
 * then front — which took the pool from the +2 it costs opaque to +4. A flat
 * quad has nothing to sort against itself, so the second pass buys nothing and
 * the flag turns it off. Measured: firing draw calls back to 43 against the
 * authentic path's 44.
 */

import * as THREE from "three/webgpu";
import { TRACER_COOL_COLOUR, TRACER_HOT_COLOUR } from "../flight/tracer-tint.ts";

/** The per-instance mid rung, read by both materials. Named once so the shader and the writer cannot drift. */
export const TRACER_TINT_ATTRIBUTE = "instanceTint";

/**
 * How much light a tracer adds at its hottest point, in units of the ramp's own
 * colour.
 *
 * Above 1 on purpose. The head is a *light source* being added to whatever is
 * behind it, and the frame it is added to is a bright daylit sky at noon — a
 * unit-strength add is a barely-perceptible tint over `#7db6c3`. Overdriving
 * the core is also what gives the glow a hard white centre without a second
 * pass: the ramp saturates in the middle and the falloff colours the ring
 * around it, which is what a bright point actually looks like.
 */
export const TRACER_HEAD_GAIN = 2.6;

/** The tail is a trail, not a source: it reads under the head rather than competing with it. */
export const TRACER_TAIL_GAIN = 1.15;

/**
 * The coverage cut — the same half every other pool in this renderer uses, and
 * exact here because the alpha it cuts is already a `step`, i.e. 0 or 1 and
 * never in between. What decides *where* that step falls is
 * `TRACER_VISIBLE_FLOOR`, not this.
 */
const TRACER_ALPHA_TEST = 0.5;

/**
 * How tightly the tail's white flare is confined to the round itself, as the
 * power the ramp's `hot` coordinate is raised to before it mixes in
 * `TRACER_HOT_COLOUR`.
 *
 * **This is the tail's half of the 2026-08-25 legibility fix**, and it changed a
 * look decision rather than a colour. It was `2`, which put the shared white
 * across most of the ribbon — and the ribbon's intensity is `hot³`, so most of
 * the ribbon is also where all of its light is. Integrated over the quad the
 * per-side tint carried **26.6 %** of the tail's emitted luminance and the two
 * shared rungs 73.4 %: a stream whose colour was three-quarters the colour
 * every other stream also is.
 *
 * At `8` it is **57.3 % tint** against 42.7 % shared. The white is still there
 * and still reads as a flare at the round — it is the one thing the head quad is
 * sitting on top of — but it is a flare rather than the whole ribbon, and the
 * body of the tail now carries the side. Eight rather than sixteen because it is
 * three squarings and because it still leaves the two shared rungs together a
 * clear two-fifths of the ribbon's light; past it the tail starts reading as a
 * flat coloured bar with a dot on the end.
 */
export const TRACER_TAIL_WHITE_EXPONENT = 8;

/**
 * Intensity below which a fragment is discarded instead of drawn: under one
 * part in 250, which is finer than a VGA DAC step (4/255) and therefore below
 * anything the picture around it could express. Nothing depends on the cut —
 * additively, adding a thousandth is the same as adding nothing — so this is a
 * shading saving over the empty corners of the quad and not a look decision.
 */
const TRACER_VISIBLE_FLOOR = 0.004;

/**
 * The instance's mid rung, as a shader node.
 *
 * A plain `TSL.attribute` and no custom node class: this renderer marks an
 * `InstancedBufferAttribute` on the geometry `stepMode: "instance"` by itself,
 * which `three/screen-sprites.ts` and `three/particle-cloud.ts`'s
 * `instanceTint` both already rely on. Reading it costs the two materials
 * nothing they were not already paying — it replaces a uniform read with a
 * vertex-attribute read in the same expression, and the mesh count is
 * unchanged, so the pool still draws in **two calls** however many sides are
 * shooting.
 */
function tierTint(): THREE.Node<"vec3"> {
  return THREE.TSL.attribute<"vec3">(TRACER_TINT_ATTRIBUTE, "vec3");
}


/** The shared opaque/additive flags — the four this renderer has already broken twice. */
function tracerMaterialBase(): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  material.blending = THREE.AdditiveBlending;
  // Both of these are the flags this renderer has broken four times, and both
  // are here because they were A/B'd on the swapchain rather than reasoned
  // about — see the module doc for the frame captures.
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = true;
  // A flat quad gains nothing from the back-then-front pass a double-sided
  // transparent material otherwise gets, and it costs a draw call per mesh.
  material.forceSinglePass = true;
  material.alphaTest = TRACER_ALPHA_TEST;
  // A tracer is light in air, not a lit surface — the same call the beams, the
  // particles and the authentic rounds all make.
  material.fog = false;
  return material;
}

/**
 * The tail ribbon's shading. Exported so `apps/web/test/tracer-material.test.ts`
 * can pin the depth and blending flags without a GPU, the arrangement
 * `bulletTracerMaterial` and `searchlightBeamMaterial` already have.
 *
 * `positionGeometry.x` runs −0.5 at the far end of the tail to +0.5 at the
 * round, because `fillTracerPool` puts the ribbon's centre half a tail *behind*
 * the head along the flight direction and the instance matrix maps the quad's
 * own `+x` onto that direction. So `hot` below is 0 at the tip and 1 at the
 * round, and everything ramps that way round.
 */
export function tracerTailMaterial(): THREE.MeshBasicNodeMaterial {
  const material = tracerMaterialBase();
  const hot = THREE.TSL.positionGeometry.x.add(0.5).clamp();
  // Cubed: the burn is concentrated just behind the round and the rest of the
  // tail is a thin fading trail, rather than a uniform bar with soft ends.
  const along = hot.mul(hot).mul(hot);
  // Across the ribbon, 0 at the centreline and 1 at either edge.
  const across = THREE.TSL.positionGeometry.y.mul(2).abs().clamp();
  const edge = THREE.TSL.smoothstep(1, 0, across);
  const intensity = along.mul(edge).mul(TRACER_TAIL_GAIN);
  // The mid rung is per-instance and the two ends are not, so the ribbon's hot
  // end and its dying tip are the same for every side and the body between them
  // carries the tint. `TRACER_TAIL_WHITE_EXPONENT` is how much of the ribbon
  // "the hot end" means, and it is the tail's half of the legibility fix.
  const white = powOf(hot, TRACER_TAIL_WHITE_EXPONENT);
  const colour = THREE.TSL.mix(
    THREE.TSL.color(TRACER_COOL_COLOUR),
    THREE.TSL.mix(tierTint(), THREE.TSL.color(TRACER_HOT_COLOUR), white),
    hot,
  );
  material.colorNode = THREE.TSL.vec4(colour.mul(intensity), THREE.TSL.step(TRACER_VISIBLE_FLOOR, intensity));
  return material;
}

/**
 * `node` raised to a power of two, by repeated squaring.
 *
 * `TSL.pow` would do it in one call, but a `pow` with a constant exponent is a
 * transcendental where three multiplies will do, and this one is evaluated per
 * fragment of every tracer in the sky. `exponent` must be a power of two; the
 * only caller passes `TRACER_TAIL_WHITE_EXPONENT`, and the test pins that it is.
 */
function powOf(node: THREE.Node<"float">, exponent: number): THREE.Node<"float"> {
  let result = node;
  for (let squarings = exponent; squarings > 1; squarings /= 2) result = result.mul(result);
  return result;
}

/**
 * The head glow's shading: a hot core inside a soft halo, on a camera-facing
 * quad of constant apparent size.
 *
 * Two falloffs summed rather than one, because a single curve cannot be both.
 * The wide one (`^3`) is the halo that makes the round findable against a
 * bright sky and visible at night; the tight one (`^14`) is the white-hot
 * centre, and it is what stops the halo reading as a fuzzy blob. Adding them
 * and letting the ramp saturate is the cheap version of a bloom pass.
 *
 * ## The two falloffs are two *lights*, not one light and a mix weight
 *
 * This is the head's half of the 2026-08-25 legibility fix, and it is a
 * rearrangement rather than a new term: **the same two curves, the same summed
 * intensity, the same total light out**. What changed is which colour each curve
 * carries.
 *
 * It used to compute one `intensity` and then pick a colour by mixing
 * `tint -> TRACER_HOT_COLOUR` on the **falloff** — so the tint was strongest at
 * the rim, where the falloff had already taken the intensity to nothing, and the
 * shared white owned the centre where all the light is. Integrated over the disc
 * the tint carried **30.4 %** of the emitted luminance. The side was being
 * painted where the round was not.
 *
 * Now the halo *is* the tint and the core *is* the white, added:
 *
 * ```
 * emitted = tint · halo · gain  +  TRACER_HOT_COLOUR · 2 · core · gain
 * ```
 *
 * which is the same `(halo + 2·core)·gain` of light, redistributed — **85.8 %**
 * of it now on the tint. The structure the description above promises is not
 * only kept but sharpened: the `^14` core is a genuinely white centre rather
 * than a white centre inside a white halo, and the halo around it is the side's
 * own colour rather than a wash of it. Physically it is also the right way
 * round — a burning core is white whatever is burning, and it is the cooler
 * light around it that a compound colours.
 *
 * `intensity` survives as its own expression because the alpha still needs it:
 * the coverage mask is a `step` on total emitted intensity, and that must stay
 * blind to which side fired or the tint would change a round's *silhouette*.
 */
export function tracerHeadMaterial(): THREE.MeshBasicNodeMaterial {
  const material = tracerMaterialBase();
  // Radius from the quad's centre, 0 at the middle and 1 at the inscribed edge.
  const centred = THREE.TSL.uv().sub(0.5).mul(2);
  const radius = centred.length().clamp();
  const falloff = THREE.TSL.float(1).sub(radius);
  const halo = falloff.mul(falloff).mul(falloff);
  const core = halo.mul(halo).mul(halo).mul(halo).mul(falloff).mul(falloff);
  const emitted = tierTint()
    .mul(halo)
    .add(THREE.TSL.color(TRACER_HOT_COLOUR).mul(core).mul(2))
    .mul(TRACER_HEAD_GAIN);
  // The same sum, kept for the coverage mask alone: a round's silhouette may not
  // depend on who fired it.
  const intensity = halo.add(core.mul(2)).mul(TRACER_HEAD_GAIN);
  material.colorNode = THREE.TSL.vec4(emitted, THREE.TSL.step(TRACER_VISIBLE_FLOOR, intensity));
  return material;
}
